import { createContext, ReactNode, useContext, useState } from 'react';
import { useSelector } from 'react-redux';
import { StatusOper, StatusType } from '../../libraries/zps';
import { AppState } from '../client-redux/reducers';
import StatusOperModal from '../components/status-oper-modal';
import { Language } from '../languages/languageHandler';
import { AxiosError } from 'axios';

type StatusOperDialogParams = {
  type: StatusType;
  message: string; // Main message to display in the modal
  statusOper?: StatusOper; // Zarph API response status
  httpStatus?: { code: number | string; message: string }; // HTTP error status
  error?: string; // Generic error message or exception details,
  timeout?: number; // Optional timeout to auto-close the modal
};
export const DEFAULT_DIALOG_TIMEOUT = 5000;

type StatusOperDialogContextType = {
  showStatusOperDialog: (params: StatusOperDialogParams) => void;
  hideStatusDialog: () => void;
  handleApiError: (error: any, message?: string, timeout?: 3000) => void;
};

const StatusOperDialogContext = createContext<StatusOperDialogContextType | undefined>(undefined);

export const useStatusOperDialog = () => {
  const context = useContext(StatusOperDialogContext);
  if (!context) {
    throw new Error('useStatus must be used within StatusOperDialogProvider');
  }
  return context;
};

export const StatusOperDialogProvider = ({ children }: { children: ReactNode }) => {
  const lang = useSelector<AppState, Language>(s => s.lang.lang);

  const [state, setState] = useState<StatusOperDialogParams & { opened: boolean }>({
    type: StatusType.INFO,
    message: '',
    opened: false,
    timeout: DEFAULT_DIALOG_TIMEOUT
  });

  const reset = () => {
    setState(prev => ({
      ...prev,
      opened: false
    }));
  };

  const showStatusOperDialog = (params: StatusOperDialogParams) => {
    const hasDetails = params.statusOper || params.httpStatus || params.error;

    if (!hasDetails) {
      console.warn('showStatus called without details:', params);
      return;
    }

    const shouldAutoClose =
      params.type === StatusType.INFO && !params.statusOper && !params.httpStatus && !params.error;

    setState({
      opened: true,
      type: params.type,
      statusOper: params.statusOper,
      httpStatus: params.httpStatus,
      message: params.message,
      error: params.error,
      timeout:
        params.timeout === 0 ? undefined : params.timeout ?? (shouldAutoClose ? DEFAULT_DIALOG_TIMEOUT : undefined)
    });
  };

  const handleApiError = (error: any, message?: string) => {
    console.log('handleApiError:  message ', message, ' API Error:', error);
    let httpStatus = undefined;
    let exceptionText = undefined;
    let responseData = undefined;

    if (error instanceof AxiosError) {
      let errorMessage = error as AxiosError;
      console.log('handleApiError: error as AxiosError:', errorMessage?.code, errorMessage?.message);

      if (error?.response && error?.response?.status !== 200) {
        console.log('handleApiError: HTTP error response.status:', error.response?.status);
        httpStatus = {
          code: error.response.status,
          message: error.response.statusText || lang.GLOBAL_ERROR
        };
      }

      if (error?.code) {
        console.log('handleApiError: Axios error code:', error.code);
        exceptionText = `${error.code}: ${error.message}`;
      }

      responseData = error?.response?.data;
    } else {
      console.log('handleApiError: Non-Axios error:', exceptionText);
      exceptionText = error?.message || String(error);
      responseData = error?.data;
    }

    showStatusOperDialog({
      type: StatusType.ERROR,
      message: message || lang.GLOBAL_ERROR,
      httpStatus,
      statusOper: responseData?.statusOper,
      error: exceptionText
    });
  };

  return (
    <StatusOperDialogContext.Provider
      value={{ showStatusOperDialog: showStatusOperDialog, hideStatusDialog: reset, handleApiError }}
    >
      {children}
      <StatusOperModal
        opened={state.opened}
        onClose={reset}
        type={state.type}
        message={state.message}
        httpStatus={state.httpStatus}
        statusOper={state.statusOper}
        error={state.error}
        timeout={state.timeout}
      />
    </StatusOperDialogContext.Provider>
  );
};

export const isErrorResponse = (response: any): boolean => {
  if (!response) return true; // treat null/undefined as error
  if (response?.isAxiosError === true) return true; // Axios error
  if (response?.status && response.status !== 200 && response.status !== 204) return true; // HTTP error status
  return false; // otherwise, not an error response
};

export const isZarphSuccessResponse = (response: any, allowed?: number[]): boolean => {
  if (isErrorResponse(response)) return false; // If it's an error response, it's not a success
  if (response?.data?.statusOper?.code === 0 || (allowed && allowed.includes(response?.data?.statusOper?.code)))
    return true; // Zarph success status
  return false; // Otherwise, not a success response
};
